import { useCookies } from 'react-cookie'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { VIEW } from 'types/deck'

import { useApplyBodyClass } from 'hooks/useScrollEffects'
import { useActionless, useAppSelector } from 'redux/hooks'
import ThemeDropdown from 'components/misc/GlobalToolbar/ThemeDropdown'
import { useHideFloatingToolbar, useLeftControls } from 'hooks/useBrowserSettings'
import { useSwipeEffects } from 'hooks/useSwipeEffects'

import FloatingToolbarListOption from 'components/elements/FloatingToolbar/ListItem'
import AnimatedHamburger from 'components/misc/AnimatedHamburger'
import AvatarCircle from 'components/user/AvatarCircle'
import ToggleSwitch from 'components/formElements/ToggleSwitch'
import AccordionItem from 'components/elements/FloatingToolbar/AccordionItem'

import { clamp } from 'utils/numbers'
import { getDeckSearchOrderingQueryParameter } from 'services/accountSettings.service'
import { logout } from 'services/cookie.service'
import { useBuildCollectionUrl } from 'utils/queryParams/collection.queryParams'

import styles from './navigationDrawer.module.scss'

const NavigationDrawer = () => {
  const router = useRouter()
  const navbar = useRef<HTMLDivElement>(null)

  const [{ tbId: userId, tbUser: username, tbRootFolder: rootFolder, tbAvatar: avatar }] = useCookies([
    'tbId',
    'tbUser',
    'tbRootFolder',
    'tbAvatar',
  ])

  const navigrationDrawerOpen = useAppSelector(state => state.active.navigationDrawerOpen)

  useApplyBodyClass(navigrationDrawerOpen, 'navdrawer-open')

  const [leftHandMode, setLeftHandMode] = useLeftControls()
  const [staticFloatingToolbar, setStaticFloatingToolbar] = useHideFloatingToolbar()
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const allDecks = `/search/decks?${getDeckSearchOrderingQueryParameter()}`
  const myFolder = `/folders/${rootFolder}`
  const sharedWithMe = `/search/decks?shared=true&${getDeckSearchOrderingQueryParameter()}`
  const bookmarkedDecks = `/search/decks?bookmarks=true&${getDeckSearchOrderingQueryParameter()}`
  const collectionUrl = useBuildCollectionUrl()

  const handleLogout = () => {
    logout()

    if (!router.pathname.includes('account')) router.reload()
    else router.push('/')
  }

  useEffect(() => {
    if (!navbar.current) return

    if (!navigrationDrawerOpen) navbar.current.removeAttribute('style')
    if (navigrationDrawerOpen && navbar.current) navbar.current.style.transform = 'translateX(0)'
  }, [navigrationDrawerOpen])

  return (
    <>
      <div
        id="navigation-drawer-backdrop"
        className={`${styles.backdrop} ${navigrationDrawerOpen ? styles.backdropOpen : ''}`}
        onClick={() => setActiveState({ navigationDrawerOpen: false })}
      />

      <nav ref={navbar} className={styles.drawer} id="navigation-drawer">
        <div className={styles.header}>
          <button onClick={() => setActiveState({ navigationDrawerOpen: false })} className={styles.closeButton}>
            <AnimatedHamburger open={navigrationDrawerOpen} />
          </button>
        </div>

        {!!userId && <AvatarCircle src={avatar} className={styles.avatar} />}

        <div className={styles.list}>
          {!!userId && (
            <AccordionItem
              primary={{ icon: 'user circle outline', label: `${username}'s stuff` }}
              options={[
                { icon: 'folder outline', label: 'Decks', to: `/folders/${rootFolder}` },
                { icon: 'address book outline', label: 'Collection', to: collectionUrl },
                {
                  icon: 'pin',
                  label: 'Pinned cards',
                  onClick: () => {
                    setActiveState({ pinnedCardsOverlay: true })
                    setActiveState({ navigationDrawerOpen: false })
                  },
                },
                { icon: 'globe', label: 'Personal categories', to: '/personal-categories' },
                { icon: 'image outline', label: 'Custom cards', to: '/custom-cards' },
                { icon: 'boxes', label: 'Packages', to: `/search/decks/?ownerUsername=${username}&packages=true` },
                { icon: 'cog', label: 'Account settings', to: '/accountSettings/site-settings' },
              ]}
            />
          )}

          <span className={styles.spacer} />

          {!!userId && <FloatingToolbarListOption hidden={!userId} label="New deck" icon="add" to="/new-deck" />}
          {!!userId && (
            <FloatingToolbarListOption hidden={!userId} label="New card package" icon="cube" to="/new-package" />
          )}
          <FloatingToolbarListOption label="Deck sandbox" icon="flask" to="/sandbox" />
          <FloatingToolbarListOption label="Random commander" icon="shuffle" to="/random-commander" />
          <span className={styles.spacer} />

          <FloatingToolbarListOption
            icon="search"
            label="Card search"
            onClick={() => setActiveState({ navigationDrawerOpen: false, searchOpen: 'search' })}
          />
          <span className={styles.spacer} />

          <AccordionItem
            openHeight={!userId ? 210 : 315} // this is fixing some stupid safari mobile bug. Remove to see if it's fixed later
            primary={{ label: 'Deck search' }}
            options={[
              { hidden: !userId, icon: 'sliders horizontal', label: 'My decks', to: myFolder },
              { icon: 'search', label: 'All decks', to: allDecks },
              { icon: 'gift', label: 'Commander precons', to: '/commander-precons' },
              { icon: 'tags', label: 'Deck tags', to: '/tags' },
              { icon: 'cube', label: 'Card packages', to: '/card-packages' },
              { icon: 'file alternate outline', label: 'Decks with primers', to: '/primers' },
              { hidden: !userId, icon: 'users', label: 'Shared with me', to: sharedWithMe },
              { hidden: !userId, icon: 'bookmark outline', label: 'Bookmarked', to: bookmarkedDecks },
              { icon: 'exchange', label: 'Compare decks', to: '/compare' },
            ]}
          />

          <AccordionItem
            openHeight={285}
            primary={{ label: 'Community' }}
            options={[
              { to: '/community', label: 'Trending', icon: 'angle double up' },
              { to: '/news', label: 'News', icon: 'newspaper outline' },
              { to: '/contest', label: 'Contests', icon: 'trophy' },
              { to: '/forum/General/Development', label: 'Development', icon: 'bug' },
              { to: '/forum/General/General', label: 'General MTG', manaIcon: 'ability-d20' },
              { to: '/forum/General/Archidecture', label: 'Deck help', icon: 'wrench' },
              {
                to: 'https://discord.gg/GyAAANU8uJ',
                label: 'Archidekt Discord',
                icon: 'discord',
                target: '_blank',
              },
              {
                to: 'https://shop.spacecowmedia.com/',
                label: 'Merch',
                icon: 'shopping cart',
                target: '_blank',
              },
            ]}
          />

          <span className={styles.spacer} />

          {!userId && <FloatingToolbarListOption icon="user plus" label="Register" to="/register" />}
          {!userId && <FloatingToolbarListOption icon="sign in" label="Log in" to="/login" />}
          {userId && <FloatingToolbarListOption icon="log out" label="Logout" onClick={handleLogout} />}

          <span className={styles.spacer} />
        </div>

        <div className={styles.quickSettings}>
          <ThemeDropdown fullDropdown up className={styles.themeMenu} />

          <ToggleSwitch
            label="Card controls on left"
            onChange={() => setLeftHandMode(!leftHandMode)}
            checked={leftHandMode}
          />
          <ToggleSwitch
            label="Hide toolbar on scroll"
            onChange={() => setStaticFloatingToolbar(!staticFloatingToolbar)}
            checked={!staticFloatingToolbar}
          />
        </div>
      </nav>
    </>
  )
}

export default NavigationDrawer

const swipeMultiplier = 1.4
const getMinMaxNavDrawer = (value: number) => clamp(-76, 0, value)

export const useNavdrawerSwipe = () => {
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const navigationDrawerOpen = useAppSelector(state => state.active.navigationDrawerOpen)

  const layout = useAppSelector(state => state.deck.view)

  const swipeRef = useSwipeEffects('horizontal', {
    checkCanStartSwipe: () => {
      if (layout === VIEW.SPREADSHEET) return false
      if (document.documentElement.clientWidth < 600 && !document.body.classList.contains('no-scrolling')) return false

      return true
    },
    onTouchMove: (offset, direction) => {
      const screenWidth = document.documentElement.clientWidth
      const navigationDrawer = document.getElementById('navigation-drawer')

      const startingPosition = !navigationDrawerOpen ? -76 : 0
      const navbarOffset = getMinMaxNavDrawer(startingPosition + (offset / screenWidth) * swipeMultiplier * 100)

      if (!navigationDrawer) return

      if (!navigationDrawerOpen && direction === 'right') {
        navigationDrawer.style.transform = `translateX(${navbarOffset}dvw)`
        navigationDrawer.style.transition = 'unset'
      }

      if (navigationDrawerOpen && direction === 'left') {
        navigationDrawer.style.transform = `translateX(${navbarOffset}dvw)`
        navigationDrawer.style.transition = 'unset'
      }
    },
    onTouchEnd: (offset, _position, speed, direction) => {
      const screenWidth = document.documentElement.clientWidth
      const navigationDrawer = document.getElementById('navigation-drawer')
      const swipeSize = (offset / screenWidth) * swipeMultiplier * 100

      if (!navigationDrawer) return

      if (!navigationDrawerOpen && direction === 'right') {
        const swipeThresholdMet = swipeSize >= 50 || speed > 0.5

        if (swipeThresholdMet) {
          setActiveState({ navigationDrawerOpen: true })

          navigationDrawer.style.transform = 'translateX(0)'
        }
        // Remove the transform from a partial-swipe
        else navigationDrawer.style.transform = ''

        navigationDrawer.style.transition = ''
      }

      if (navigationDrawerOpen && direction === 'left') {
        if (swipeSize < -10 || speed > 0.5) setActiveState({ navigationDrawerOpen: false })
        else setActiveState({ navigationDrawerOpen: true })

        navigationDrawer.style.transition = ''
        navigationDrawer.style.transform = ''
      }

      // Not sure I wanna do this
      // if (offset < -300 || (speed > 0.5 && offset < 0)) return setActiveState({ searchOpen: true })
    },
  })

  return swipeRef
}
